import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { salesCrmApi } from "./api";
import "./salescrm.css";

const SalesCrmDashboard = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [loading, setLoading] = useState(true);
  const [allocating, setAllocating] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const isManager = !!me?.is_manager;
  const viewingEmployeeId = isManager ? selectedEmployeeId : "";

  const load = async (employeeId = selectedEmployeeId) => {
    setLoading(true);
    setError(null);
    try {
      const meRes = await salesCrmApi.me();
      setMe(meRes.data);
      const manager = !!meRes.data.is_manager;

      let empList = [];
      if (manager) {
        const empRes = await salesCrmApi.employees({ active: "1" });
        empList = empRes.data || [];
        setEmployees(empList);
      }

      const dashParams = {};
      if (manager && employeeId) {
        dashParams.employee_id = employeeId;
      } else if (!manager) {
        dashParams.mine = "1";
      } else {
        dashParams.mine = "1";
      }
      const dashRes = await salesCrmApi.dashboard(dashParams);
      setStats(dashRes.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load Sales CRM.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!me) return;
    load(selectedEmployeeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployeeId]);

  const selectedEmployee = employees.find(
    (e) => String(e.id) === String(selectedEmployeeId)
  );

  const getTodaysLeads = async () => {
    setAllocating(true);
    setMessage(null);
    setError(null);
    try {
      const payload = {};
      if (isManager && selectedEmployeeId) {
        payload.employee_id = Number(selectedEmployeeId);
      }
      const res = await salesCrmApi.getTodaysLeads(payload);
      const d = res.data;
      const forName = d.employee_name || selectedEmployee?.name || "you";
      if (d.already_allocated) {
        setMessage(
          `Today's ${d.allocated} leads are already assigned to ${forName}. Opening the list.`
        );
      } else {
        setMessage(
          `Assigned ${d.allocated} fresh leads to ${forName}` +
            (d.shortage ? ` (${d.shortage} short of the daily limit).` : ".")
        );
      }
      await load(selectedEmployeeId);
      const q = new URLSearchParams({ bucket: "today" });
      if (isManager && selectedEmployeeId) {
        q.set("employee_id", selectedEmployeeId);
        q.set("scope", "all");
      }
      navigate(`/dashboard/sales-crm/leads?${q.toString()}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not allocate leads.");
    } finally {
      setAllocating(false);
    }
  };

  const leadsQuery = (bucket) => {
    const q = new URLSearchParams({ bucket });
    if (isManager && selectedEmployeeId) {
      q.set("employee_id", selectedEmployeeId);
      q.set("scope", "all");
    } else if (isManager) {
      q.set("scope", "all");
    }
    return `/dashboard/sales-crm/leads?${q.toString()}`;
  };

  const cards = [
    { key: "today", label: "Today's new leads", value: stats?.today_new_leads, bucket: "today" },
    { key: "untouched", label: "Untouched today", value: stats?.untouched_today, bucket: "today", cls: "warn" },
    { key: "backlog", label: "Untouched backlog", value: stats?.backlog, bucket: "backlog" },
    { key: "hot", label: "Hot leads", value: stats?.hot_leads, bucket: "hot", cls: "hot" },
    { key: "fu", label: "Follow-ups today", value: stats?.follow_ups_today, bucket: "follow_ups" },
    { key: "overdue", label: "Overdue follow-ups", value: stats?.follow_ups_overdue, bucket: "overdue_follow_ups", cls: "warn" },
    { key: "interested", label: "Interested", value: stats?.interested, bucket: "interested", cls: "good" },
    {
      key: "connected",
      label: `Connected today / ${stats?.connected_target || 35}`,
      value: stats?.connected_calls_today,
      bucket: "today",
      cls: "good",
    },
    { key: "bought", label: "Bought this month", value: stats?.bought_this_month, bucket: "bought", cls: "good" },
    { key: "revenue", label: "Revenue this month", value: stats ? `₹${stats.revenue_this_month}` : "—" },
  ];

  if (loading && !stats) {
    return (
      <div className="child-sidenav">
        <div className="childnav-content scrm-page">Loading Sales CRM…</div>
      </div>
    );
  }

  return (
    <div className="child-sidenav">
      <div className="childnav-content scrm-page">
        <div className="scrm-header">
          <div>
            <h2>Sales CRM</h2>
            <p>
              Welcome{me?.name ? `, ${me.name}` : ""}. Fresh leads, backlog and
              follow-ups stay separate so nothing is lost overnight.
            </p>
          </div>
          <div className="scrm-actions">
            <button
              type="button"
              className="scrm-btn scrm-btn-primary"
              onClick={getTodaysLeads}
              disabled={allocating}
            >
              {allocating
                ? "Allocating…"
                : isManager && selectedEmployee
                  ? `Get Today's Leads for ${selectedEmployee.name}`
                  : "Get Today's Leads"}
            </button>
            <button
              type="button"
              className="scrm-btn scrm-btn-outline"
              onClick={() => navigate(leadsQuery("today"))}
            >
              Today's list
            </button>
            {isManager && (
              <button
                type="button"
                className="scrm-btn scrm-btn-ghost"
                onClick={() => navigate("/dashboard/sales-crm/admin")}
              >
                Manager console
              </button>
            )}
          </div>
        </div>

        {isManager && (
          <div className="scrm-card">
            <h3>Work as / filter by staff</h3>
            <p className="scrm-muted" style={{ marginBottom: 10 }}>
              As super admin you can pull today's leads for any staff member and
              view their queues. Leave as “All team” to see combined stats.
            </p>
            <div className="scrm-toolbar">
              <select
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                style={{ minWidth: 260 }}
              >
                <option value="">All team (overview)</option>
                <option value={String(me?.id || "")}>
                  Myself ({me?.name || "me"})
                </option>
                {employees
                  .filter((e) => String(e.id) !== String(me?.id))
                  .map((e) => (
                    <option key={e.id} value={String(e.id)}>
                      {e.name} {e.role === "manager" ? "(manager)" : ""} · limit{" "}
                      {e.daily_lead_limit}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                className="scrm-btn scrm-btn-outline"
                onClick={() => navigate(leadsQuery("all"))}
              >
                View leads
                {selectedEmployee ? ` · ${selectedEmployee.name}` : " · all staff"}
              </button>
            </div>
          </div>
        )}

        {message && <div className="scrm-alert ok">{message}</div>}
        {error && <div className="scrm-alert err">{error}</div>}

        <div className="scrm-alert info">
          Daily target: at least <b>{stats?.connected_target || 35}</b> genuine
          connected conversations
          {selectedEmployee ? ` for ${selectedEmployee.name}` : ""}. Untouched
          fresh leads automatically move to backlog tomorrow — they are never
          deleted.
        </div>

        <div className="scrm-stats">
          {cards.map((c) => (
            <div
              key={c.key}
              className={`scrm-stat ${c.cls || ""}`}
              onClick={() => c.bucket && navigate(leadsQuery(c.bucket))}
              role={c.bucket ? "button" : undefined}
            >
              <div className="label">{c.label}</div>
              <div className="value">{c.value ?? "—"}</div>
            </div>
          ))}
        </div>

        <div className="scrm-card">
          <h3>Quick links</h3>
          <div className="scrm-actions">
            <button type="button" className="scrm-btn scrm-btn-ghost" onClick={() => navigate(leadsQuery("hot"))}>Hot leads</button>
            <button type="button" className="scrm-btn scrm-btn-ghost" onClick={() => navigate(leadsQuery("follow_ups"))}>Follow-ups</button>
            <button type="button" className="scrm-btn scrm-btn-ghost" onClick={() => navigate(leadsQuery("backlog"))}>Backlog</button>
            <button type="button" className="scrm-btn scrm-btn-ghost" onClick={() => navigate(leadsQuery("interested"))}>Interested</button>
            <button type="button" className="scrm-btn scrm-btn-ghost" onClick={() => navigate(leadsQuery("bought"))}>Bought</button>
            <button type="button" className="scrm-btn scrm-btn-ghost" onClick={() => navigate(leadsQuery("not_interested"))}>Not interested</button>
            <button type="button" className="scrm-btn scrm-btn-outline" onClick={() => navigate("/dashboard/sales-crm/manual")}>Add manual lead</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { SalesCrmDashboard };
